import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PlusCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import ImportUsersDialog from './import/ImportUsersDialog';
import { AuthorizedUser } from '@/types/authorizedUser';
import UserSearch from './users/UserSearch';
import AddUserForm from './users/AddUserForm';
import UsersTable from './users/UsersTable';

interface AuthorizedUsersTableProps {
  users: AuthorizedUser[];
  onAddUser?: (user: Omit<AuthorizedUser, 'id'>) => Promise<void>;
  onDeleteUser?: (userId: string) => Promise<void>;
  onBulkImport?: (users: Omit<AuthorizedUser, 'id'>[]) => Promise<void>;
}

const AuthorizedUsersTable: React.FC<AuthorizedUsersTableProps> = ({
  users,
  onAddUser,
  onDeleteUser,
  onBulkImport,
}) => {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    accountNumber: '',
    licenseNumber: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.accountNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.licenseNumber.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddUser = async () => {
    // Basic validation
    if (!newUser.name || !newUser.email || !newUser.accountNumber || !newUser.licenseNumber) {
      toast({
        title: "Missing Information",
        description: "Please fill in all fields for the new authorized user",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      if (onAddUser) {
        await onAddUser(newUser);
      }
      
      // Reset form
      setNewUser({
        name: '',
        email: '',
        accountNumber: '',
        licenseNumber: '',
      });
      setShowAddForm(false);
      
      toast({
        title: "User Added",
        description: "The authorized user has been added successfully",
      });
    } catch (error) {
      console.error('Error adding user:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add user",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      if (onDeleteUser) {
        await onDeleteUser(userId);
      }
      
      toast({
        title: "User Removed",
        description: "The authorized user has been removed",
      });
    } catch (error) {
      console.error('Error deleting user:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to remove user",
        variant: "destructive",
      });
    }
  };

  const handleImportSuccess = async (importedUsers: AuthorizedUser[]) => {
    try {
      if (onBulkImport) {
        // Extract data without id since we're passing to onBulkImport
        const usersWithoutIds = importedUsers.map(({ id, ...userData }) => userData);
        await onBulkImport(usersWithoutIds);
      } else if (onAddUser) {
        // Fallback to processing each user individually if onBulkImport isn't provided
        for (const user of importedUsers) {
          const { id, ...userData } = user;
          await onAddUser(userData);
        }
      }
    } catch (error) {
      console.error('Error processing imported users:', error);
      toast({
        title: "Import Processing Error",
        description: error instanceof Error ? error.message : "Failed to process some imported users",
        variant: "destructive",
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <CardTitle>Authorized Users</CardTitle>
            <CardDescription>Users authorized to register in the referral program</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowAddForm(!showAddForm)}
              className="flex items-center"
            >
              <PlusCircle className="mr-2 h-4 w-4" />
              Add User
            </Button>
            <ImportUsersDialog onImportSuccess={handleImportSuccess} />
          </div>
        </div>
        
        <UserSearch searchTerm={searchTerm} onSearchChange={setSearchTerm} />
      </CardHeader>
      <CardContent>
        {showAddForm && (
          <AddUserForm
            newUser={newUser}
            onChange={setNewUser}
            onSubmit={handleAddUser}
            onCancel={() => setShowAddForm(false)}
            isSubmitting={isSubmitting}
          />
        )}
        
        {filteredUsers.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {searchTerm ? "No users match your search" : "No authorized users found"}
          </div>
        ) : (
          <UsersTable users={filteredUsers} onDeleteUser={handleDeleteUser} />
        )}
      </CardContent>
    </Card>
  );
};

export default AuthorizedUsersTable;
